import { defineStore } from 'pinia'
import {
  getCategories,
  storeCategory,
  updateCategory,
  destroyCategory,
} from '@/api/categories.api'
import { useSwal } from '@/composables'
import { extractClapError } from '@/helpers/errorHelper'

export type Category = {
  _id: string
  name: string
}

type State = {
  items: Category[]
  count: number
}

function parseCategoriesResponse(data: unknown): {
  categories: Category[]
  count: number
} {
  if (Array.isArray(data)) {
    return { categories: data, count: data.length }
  }
  const payload = (data || {}) as { categories?: Category[]; count?: number }
  const categories = payload.categories || []
  return { categories, count: payload.count ?? categories.length }
}

export const useCategoriesStore = defineStore('categories', {
  state: (): State => ({
    items: [],
    count: 0,
  }),
  getters: {
    categories: (state): Category[] => state.items,
  },
  actions: {
    setCategories(categories: Category[]) {
      this.items = categories
    },
    setCount(count: number) {
      this.count = count
    },
    async indexCategories() {
      try {
        const { count, categories } = parseCategoriesResponse(
          await getCategories()
        )
        this.setCount(count || 0)
        this.setCategories(categories || [])
        return categories || []
      } catch (e) {
        this.setCount(0)
        this.setCategories([])
        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        console.log(e)
        return []
      }
    },
    async storeCategory(category: Pick<Category, 'name'>) {
      try {
        const newCategory = await storeCategory(category)
        if (newCategory && !this.items.some(c => c._id === newCategory._id)) {
          this.setCategories([...this.items, newCategory])
        }
        return newCategory
      } catch (e) {
        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        console.log(e)
      }
    },
    async updateCategory(category: Category) {
      try {
        await updateCategory(category)
        this.setCategories(
          this.items.map(c => (c._id === category._id ? category : c))
        )
      } catch (e) {
        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        console.log(e)
      }
    },
    async deleteCategory(id: string) {
      try {
        await destroyCategory(id)
        this.setCategories(this.items.filter(c => c._id !== id))
      } catch (e) {
        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        console.log(e)
        throw e
      }
    },
    async replicateCategory(id: string) {
      try {
        const newCategory = await storeCategory({ name: id })
        this.setCategories([...this.items, newCategory])
      } catch (e) {
        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        console.log(e)
      }
    },
  },
})
